import React from "react";
import { PathItem } from "../Api/types";

/**
 * Props for ContentManagerBreadcrumbs component
 * @typedef {Object} ContentManagerBreadcrumbsProps
 * @property {PathItem[]} items - Array of PathItem objects representing the breadcrumbs
 * @property {boolean} hasFullPath - Flag indicating whether the current path has a full path
 * @property {(item: PathItem, index: number) => void} onNavigate - Callback function to handle navigation
 */
type ContentManagerBreadcrumbsProps = {
    items: PathItem[];
    hasFullPath: boolean;
    onNavigate: (item: PathItem, index: number) => void;
};

/**
 * Component to display breadcrumbs for content navigation
 * @param items
 * @param hasFullPath
 * @param onNavigate
 * @constructor
 * @returns
 */
const ContentManagerBreadcrumbs: React.FC<ContentManagerBreadcrumbsProps> = ({
    items,
    hasFullPath,
    onNavigate,
}) => {
    if (items.length === 0) {
        return null;
    }

    return (
        <nav className="breadcrumb" aria-label="Content location">
            <ol className="breadcrumb-track">
                {items.map((item, index) => {
                    const isLast = index === items.length - 1;
                    const normalizedListId = item.listId?.trim();
                    const hasListId = Boolean(normalizedListId);
                    const displayLabel =
                        item.title?.trim() || normalizedListId || `Item ${index + 1}`;

                    const breadcrumbKey: string = hasListId ? normalizedListId : `id-${item.id}`;

                    return (
                        <li
                            key={breadcrumbKey}
                            className={`breadcrumb-item${isLast ? " breadcrumb-item--current" : ""}`}
                        >
                            <div className="breadcrumb-node">
                                {isLast ? (
                                    <span className="breadcrumb-current" title={displayLabel}>
                                        <span className="breadcrumb-dot" aria-hidden="true" />
                                        <span className="breadcrumb-label" aria-current="page">
                                            {displayLabel}
                                        </span>
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        className="breadcrumb-link"
                                        onClick={() => onNavigate(item, index)}
                                        disabled={!hasFullPath}
                                        title={displayLabel}
                                        aria-label={`Go to ${displayLabel}`}
                                    >
                                        <span className="breadcrumb-dot" aria-hidden="true" />
                                        <span className="breadcrumb-label">{displayLabel}</span>
                                    </button>
                                )}
                            </div>
                            {!isLast && (
                                <span className="breadcrumb-separator" aria-hidden="true">
                                    <svg
                                        role="img"
                                        aria-hidden="true"
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M9 6l6 6-6 6"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </span>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};

export default ContentManagerBreadcrumbs;
